import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useDeleteCredential } from '../../hooks/useCredentials';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const DeleteCredentialModal = ({ isOpen, onClose, credential, supplierUid }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const deleteCredential = useDeleteCredential(supplierUid);
    const { t } = useLanguage();

    const handleDelete = async () => {
        setDeleteError('');
        setIsDeleting(true);
        try {
            await deleteCredential.mutateAsync(credential.uid);
            onClose();
        } catch (error) {
            console.error('Error deleting credential:', error);
            setDeleteError(error.message || t(KEYS.CREDENTIAL_DELETE_ERROR));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) {
            setDeleteError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.CREDENTIAL_DELETE_TITLE)}
            size="small"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isDeleting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="danger" size="medium" onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? t(KEYS.CREDENTIAL_DELETE_SUBMITTING) : t(KEYS.COMMON_DELETE)}
                    </Button>
                </>
            }
        >
            <div className="p-4 space-y-3">
                {deleteError && (
                    <div className="p-3 bg-status-overdue-bg text-status-overdue text-sm rounded-md border border-status-overdue/20">
                        {deleteError}
                    </div>
                )}

                <div className="flex gap-3 p-3 rounded-md bg-status-overdue-bg border border-status-overdue/20">
                    <AlertTriangle className="w-5 h-5 text-status-overdue shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm text-text-primary">
                            Are you sure you want to delete{' '}
                            <span className="font-semibold">{credential?.title || 'this credential'}</span>?
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                            This action cannot be undone. The stored username and password will be permanently removed.
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default DeleteCredentialModal;
